export type OutboundCallDraft = {
  voiceAgentId: string;
  customerName: string;
  customerPhone: string;
  /** When true, scheduledAt is required by the API. When false, the call is placed immediately. */
  scheduleLater: boolean;
  scheduledAt: string;
  scheduledTimezone: string;
  callObjective: string;
  customerContext: string;
  guidelines: string;
  callbackPhone: string;
  leaveVoicemail: boolean;
  /** TODO: UI/ingestion deferred — kept for schema compatibility; always []. */
  fileIds: string[];
};

/**
 * Builds the GraphQL CreateOutboundCallInput / UpdateOutboundCallInput payload from a draft.
 * Used by the outbound-call data handlers and the Copilot artifact card.
 */
export const buildOutboundCallMutationInput = (draft: OutboundCallDraft) => {
  const scheduledAtRaw = draft.scheduledAt?.trim();
  let scheduledAt: Date | null = null;
  if (draft.scheduleLater && scheduledAtRaw) {
    scheduledAt = datetimeLocalInTimezoneToUtc(scheduledAtRaw, draft.scheduledTimezone);
  }
  return {
    voiceAgentId: draft.voiceAgentId,
    customer: { name: draft.customerName || null, phone: draft.customerPhone },
    schedule: {
      scheduleLater: draft.scheduleLater,
      scheduledAt: draft.scheduleLater ? scheduledAt : null,
      timezone: draft.scheduledTimezone
    },
    callConfig: {
      callObjective: draft.callObjective,
      customerContext: draft.customerContext || null,
      guidelines: draft.guidelines || null,
      callbackPhone: draft.callbackPhone.trim() || null,
      leaveVoicemail: draft.leaveVoicemail,
      fileIds: draft.fileIds ?? []
    }
  };
};

/**
 * Interprets a datetime-local wall clock (YYYY-MM-DDTHH:mm) as occurring in
 * `timeZone` and returns the corresponding UTC Date.
 */
export const datetimeLocalInTimezoneToUtc = (
  datetimeLocal: string,
  timeZone: string
): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(datetimeLocal.trim());
  if (!match || !timeZone.trim()) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hour = Number(match[4]);
  const minute = Number(match[5]);
  if ([year, month, day, hour, minute].some((n) => Number.isNaN(n))) {
    return null;
  }

  // Guess wall time as UTC, then correct by the zone offset at that instant.
  let utcMs = Date.UTC(year, month - 1, day, hour, minute, 0);
  for (let i = 0; i < 2; i++) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(new Date(utcMs));
    const get = (type: Intl.DateTimeFormatPartTypes) =>
      Number(parts.find((part) => part.type === type)?.value);
    const asWallUtc = Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour'),
      get('minute'),
      get('second') || 0
    );
    const desiredWallUtc = Date.UTC(year, month - 1, day, hour, minute, 0);
    utcMs += desiredWallUtc - asWallUtc;
  }

  const result = new Date(utcMs);
  return Number.isNaN(result.getTime()) ? null : result;
};
